// Reusable UI primitives (§1.4), all token-backed so they re-skin with the theme.
// Toggle, SegmentedControl, Chip, StatCard, Toast, Modal, BottomSheet.
import React from "react";

/* ------------------------------------------------------------------ Toggle */
export interface ToggleProps {
  on: boolean;
  onChange: (next: boolean) => void;
  label?: string;
  id?: string;
}

export const Toggle: React.FC<ToggleProps> = ({ on, onChange, label, id }) => (
  <button
    id={id}
    className="mc-tap"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={() => onChange(!on)}
    style={{
      width: 46,
      height: 28,
      flex: "0 0 auto",
      borderRadius: 999,
      border: "none",
      cursor: "pointer",
      position: "relative",
      transition: "background .2s ease",
      background: on ? "var(--accent)" : "var(--track)",
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 3,
        left: on ? 21 : 3,
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: "#fff",
        transition: "left .2s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
      }}
    />
  </button>
);

/* -------------------------------------------------------- SegmentedControl */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}
export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: "flex",
        gap: 6,
        padding: 4,
        borderRadius: 12,
        background: "var(--fill)",
        border: "1px solid var(--border)",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            aria-selected={active}
            className="mc-tap"
            onClick={() => onChange(o.value)}
            style={{
              flex: 1,
              minHeight: 44,
              border: "none",
              borderRadius: 9,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              fontWeight: 700,
              color: active ? "#fff" : "var(--text-2)",
              background: active ? "var(--accent)" : "transparent",
              transition: "background .18s ease, color .18s ease",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------- Chip */
export interface ChipProps {
  label: string;
  active?: boolean;
  onClick?: () => void;
  color?: string;
}

export const Chip: React.FC<ChipProps> = ({ label, active, onClick, color }) => {
  const accent = color ?? "var(--accent)";
  return (
    <button
      className="mc-tap"
      onClick={onClick}
      aria-pressed={active}
      style={{
        minHeight: 36,
        padding: "0 14px",
        borderRadius: 11,
        cursor: onClick ? "pointer" : "default",
        fontSize: 13,
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: active ? "#fff" : "var(--text-2)",
        background: active ? accent : "var(--fill)",
        border: `1px solid ${active ? accent : "var(--border)"}`,
        transition: "background .16s ease, color .16s ease",
      }}
    >
      {label}
    </button>
  );
};

/* ---------------------------------------------------------------- StatCard */
export interface StatCardProps {
  value: React.ReactNode;
  label: string;
  color?: string;
}

export const StatCard: React.FC<StatCardProps> = ({ value, label, color }) => (
  <div
    style={{
      padding: 16,
      borderRadius: 16,
      background: "var(--surface)",
      border: "1px solid var(--border)",
    }}
  >
    <div
      style={{
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: 24,
        fontWeight: 700,
        color: color ?? "var(--accent-text)",
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: 12, color: "var(--text-2)" }}>{label}</div>
  </div>
);

/* ------------------------------------------------------------------- Toast */
export type ToastKind = "success" | "info" | "warning" | "error";

const TOAST_ACCENT: Record<ToastKind, string> = {
  success: "var(--green)",
  info: "var(--blue)",
  warning: "var(--amber)",
  error: "var(--pink)",
};

export interface ToastProps {
  message: string;
  kind?: ToastKind;
}

/** A single toast pill. Position it via the parent; it fills width and centers. */
export const Toast: React.FC<ToastProps> = ({ message, kind = "info" }) => {
  const accent = TOAST_ACCENT[kind];
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "absolute",
        left: 16,
        right: 16,
        bottom: 20,
        margin: "0 auto",
        maxWidth: 360,
        padding: "12px 16px",
        borderRadius: 14,
        background: "var(--surface)",
        border: `1px solid ${accent}`,
        color: "var(--text)",
        fontSize: 14,
        fontWeight: 600,
        textAlign: "center",
        boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
        zIndex: 40,
        pointerEvents: "none",
      }}
    >
      {message}
    </div>
  );
};

/* --------------------------------------------------------------- Backdrop */
const Backdrop: React.FC<{ onClick?: () => void; children: React.ReactNode; align: "center" | "end" }> = ({
  onClick,
  children,
  align,
}) => (
  <div
    onClick={onClick}
    style={{
      position: "absolute",
      inset: 0,
      zIndex: 50,
      display: "flex",
      flexDirection: "column",
      justifyContent: align,
      alignItems: "center",
      background: "rgba(0,0,0,0.6)",
      padding: align === "center" ? 20 : 0,
    }}
  >
    {children}
  </div>
);

/* ------------------------------------------------------------------- Modal */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: number;
}

export const Modal: React.FC<ModalProps> = ({ open, onClose, title, children, maxWidth = 420 }) => {
  if (!open) return null;
  return (
    <Backdrop onClick={onClose} align="center">
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          width: "100%",
          maxWidth,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 20,
          padding: 20,
          color: "var(--text)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
        }}
      >
        {title && (
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
            {title}
          </div>
        )}
        {children}
      </div>
    </Backdrop>
  );
};

/* -------------------------------------------------------------- BottomSheet */
export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <Backdrop onClick={onClose} align="end">
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          width: "100%",
          maxWidth: 480,
          background: "var(--surface)",
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          borderTop: "1px solid var(--border)",
          padding: 20,
          paddingBottom: "calc(20px + env(safe-area-inset-bottom))",
          color: "var(--text)",
          maxHeight: "80%",
          overflowY: "auto",
          boxShadow: "0 -12px 40px rgba(0,0,0,0.5)",
          animation: "mcSheetUp .22s cubic-bezier(.2,.7,.3,1)",
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            borderRadius: 999,
            background: "var(--track)",
            margin: "0 auto 14px",
          }}
        />
        {title && (
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
            {title}
          </div>
        )}
        {children}
      </div>
    </Backdrop>
  );
};
